"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { useTranslations } from "use-intl";

import { LastfmScrobbleIcon, LASTFM_BRAND_COLOR } from "@/components/icons/lastfm-scrobble-icon";
import { useViewerQueryState } from "@/hooks/use-viewer-query-state";
import {
  buildSignupHrefWithRedirect,
  navigateToLoginAfterCurrentPage,
} from "@/lib/redirect-after-login";
import { cn } from "@/lib/utils";
import { client, orpc } from "@/utils/orpc.client";

import { LastfmScrobbleDialog } from "./lastfm-scrobble-dialog";

type ScrobbleEntity = "work" | "release";

interface LastfmScrobbleButtonProps {
  entity: ScrobbleEntity;
  id: string;
  className?: string;
  showLabelOnMobile?: boolean;
  variant?: "full" | "pill";
}

export function LastfmScrobbleButton({
  entity,
  id,
  className,
  showLabelOnMobile = true,
  variant = "full",
}: LastfmScrobbleButtonProps) {
  const t = useTranslations("shared.lastfmScrobble");
  const router = useRouter();
  const queryClient = useQueryClient();
  const { session, enabledWhenAuthenticated } = useViewerQueryState();
  const [dialogOpen, setDialogOpen] = useState(false);
  const statusQueryOptions = orpc.lastfm.status.queryOptions({});

  const statusQuery = useQuery({
    ...statusQueryOptions,
    enabled: enabledWhenAuthenticated,
    staleTime: 30_000,
  });

  const connectMutation = useMutation({
    mutationFn: () => client.lastfm.connect({}),
    onSuccess: (data) => {
      window.location.href = data.authorizeUrl;
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const handleClick = () => {
    if (!session?.user) {
      void navigateToLoginAfterCurrentPage(router);
      return;
    }

    if (statusQuery.data?.accountRequired) {
      void router.navigate({
        href: buildSignupHrefWithRedirect(router.state.location.href),
      });
      return;
    }

    if (!statusQuery.data?.connected) {
      connectMutation.mutate();
      return;
    }

    setDialogOpen(true);
  };

  return (
    <>
      <button
        type="button"
        aria-label={t("button")}
        onClick={handleClick}
        disabled={connectMutation.isPending || statusQuery.isFetching}
        className={cn(
          "flex items-center justify-center border border-[#D51007]/30 bg-[#D51007]/10 text-foreground hover:bg-[#D51007]/15 transition-all disabled:opacity-50",
          variant === "full" && "flex-1 min-w-[160px] gap-2 rounded-lg h-14 text-lg font-bold",
          variant === "pill" &&
            "shrink-0 gap-1.5 rounded-full min-h-11 sm:min-h-10 px-3 md:px-4 text-sm font-medium",
          className,
        )}
      >
        <LastfmScrobbleIcon
          className={cn(variant === "full" ? "h-5 w-9" : "h-4 w-7")}
          style={{ color: LASTFM_BRAND_COLOR }}
        />
        <span className={cn(!showLabelOnMobile && "hidden md:inline")}>
          {connectMutation.isPending ? t("connecting") : t("button")}
        </span>
      </button>

      <LastfmScrobbleDialog
        entity={entity}
        id={id}
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onReconnectRequired={() => {
          queryClient.invalidateQueries({ queryKey: statusQueryOptions.queryKey });
          setDialogOpen(false);
        }}
      />
    </>
  );
}
